import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Globe,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { ProgressSteps } from "@/components/ui/progress-steps";
import api from "@/utils/api";
import { PhoneInput } from "@/components/ui/phone-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import { LogoUpload } from "@/components/ui/logo-upload";
import {
  clearClaimedStoreUrl,
  clearPendingBusinessData,
  getValidStoreHandle,
  normalizeStoreHandle,
  readClaimedStoreUrl,
  readPendingBusinessData,
  validateStoreUrl,
} from "@/utils/storeUrlValidator";
import { cn } from "@/lib/utils";
import { CURRENCY_OPTIONS } from "@/utils/currency";
import {
  AuthCard,
  AuthPageHeader,
  authButtonClass,
  authFieldClass,
  authOutlineButtonClass,
} from "@/components/auth/AuthCard";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import { useAuth } from "@/contexts/AuthContext";

import logo from "@/assets/logo.png";

const BUSINESS_TYPES = [
  { id: "restaurant", label: "Restaurant & Cafe", icon: "🍽️" },
  { id: "salon", label: "Salon & Beauty", icon: "💇‍♀️" },
  { id: "retail", label: "Retail Store", icon: "🏪" },
  { id: "fashion", label: "Fashion & Apparel", icon: "👕" },
  { id: "food", label: "Home-Based Food", icon: "🥘" },
  { id: "bakery", label: "Bakery & Pastry", icon: "🥖" },
  { id: "jewelry", label: "Jewelry & Accessories", icon: "💍" },
  { id: "pharmacy", label: "Pharmacy & Health", icon: "💊" },
  { id: "digital", label: "Digital Products", icon: "💻" },
  { id: "hospitality", label: "Hospitality", icon: "🏨" },
  { id: "car_dealer", label: "Car Dealer", icon: "🚗" },
  { id: "other", label: "Other", icon: "💡" },
  { id: "services", label: "Services & Booking", icon: "📅" },
];

const EMPLOYEE_COUNT_OPTIONS = [
  { id: "1-5", label: "1-5 employees" },
  { id: "6-10", label: "6-10 employees" },
  { id: "11-20", label: "11-20 employees" },
  { id: "21-50", label: "21-50 employees" },
  { id: "50+", label: "50+ employees" },
];

const MONTHLY_ORDERS_OPTIONS = [
  { id: "0-10", label: "0-10 orders" },
  { id: "11-50", label: "11-50 orders" },
  { id: "51-100", label: "51-100 orders" },
  { id: "101-500", label: "101-500 orders" },
  { id: "500+", label: "500+ orders" },
];

const REFERRAL_OPTIONS = [
  { id: "google", label: "Google Search" },
  { id: "friend", label: "Friend or Colleague" },
  { id: "social", label: "Social Media" },
  { id: "ad", label: "Advertisement" },
  { id: "other", label: "Other" },
];

const GOAL_OPTIONS = [
  { id: "sell_online", label: "Sell products online" },
  { id: "accept_bookings", label: "Accept bookings and appointments" },
  { id: "grow_brand", label: "Grow my brand presence" },
  { id: "manage_inventory", label: "Manage inventory effectively" },
  { id: "accept_payments", label: "Accept online payments" },
  { id: "customer_management", label: "Better customer management" },
  { id: "digital_marketing", label: "Digital marketing and promotion" },
  { id: "expand_business", label: "Expand to new markets" },
];

// Use unified currency options

export function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string>("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string>("");

  // Combined form data for all steps
  const [formData, setFormData] = useState({
    // Step 1: Business Details
    businessName: "",
    businessType: "",
    numberOfEmployees: "1-5",
    monthlyOrders: "0-10",
    referralSource: "",

    // Step 2: Goals
    goals: [] as string[],

    // Step 3: Store Information
    storeName: "",
    whatsappNumber: "",
    currency: "NGN",
    url: "",
  });

  useEffect(() => {
    const pending = readPendingBusinessData();
    const claimed =
      readClaimedStoreUrl() || getValidStoreHandle(pending.url || "");
    const name = pending.name || user?.name || "";

    setFormData((prev) => ({
      ...prev,
      businessName: prev.businessName || name,
      storeName: prev.storeName || name,
      whatsappNumber: prev.whatsappNumber || pending.whatsappNumber || "",
      url: prev.url || claimed,
    }));
  }, [user?.name]);

  const handleGoalToggle = (goalId: string) => {
    setFormData((prev) => {
      const goals = [...prev.goals];
      const index = goals.indexOf(goalId);

      if (index > -1) {
        goals.splice(index, 1);
      } else {
        goals.push(goalId);
      }

      return { ...prev, goals };
    });
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (
          !formData.businessName ||
          !formData.businessType ||
          !formData.referralSource
        ) {
          toast.error("Please fill in all required fields");
          return false;
        }
        return true;
      case 2:
        if (formData.goals.length === 0) {
          toast.error("Please select at least one goal");
          return false;
        }
        return true;
      case 3:
        if (!formData.storeName || !formData.url) {
          toast.error("Please fill in all required fields");
          return false;
        }

        if (!formData.whatsappNumber) {
          setPhoneError("Phone number is required");
          return false;
        }

        if (!isValidPhoneNumber(formData.whatsappNumber)) {
          setPhoneError("Please enter a valid phone number");
          return false;
        }

        // Validate store URL
        const urlValidation = validateStoreUrl(formData.url);
        if (!urlValidation.valid) {
          setUrlError(urlValidation.message || "Invalid URL");
          return false;
        }

        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep()) {
      return;
    }

    setIsLoading(true);
    try {
      // Create request body with form data
      const requestBody: Record<string, any> = {
        ...formData,
        logoBase64: logoBase64 || null,
      };

      // If goals is an array, we need to ensure it's properly serialized
      if (Array.isArray(requestBody.goals)) {
        requestBody.goals = requestBody.goals;
      }

      const response = await api.post(
        "/api/auth/complete-onboarding",
        requestBody
      );

      const store = response.data;

      clearClaimedStoreUrl();
      clearPendingBusinessData();

      // Generate the full store URL to pass to the share page
      const storeUrl = `https://sqale.shop/${store.url}`;

      // Navigate to the share page with the store URL and logo
      navigate("/auth/share-store", {
        state: {
          storeUrl,
          storeLogo: store.logo || "",
        },
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to complete setup");
      setIsLoading(false);
    }
  };

  // Helpers to generate unique IDs for inputs
  const getInputId = (name: string) => `onboarding-${name}`;

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = normalizeStoreHandle(e.target.value);
    setFormData({ ...formData, url: value });

    // Clear URL error when user is typing
    if (urlError) {
      setUrlError("");
    }
  };

  return (
    <AuthLayout>
      <AuthCard wide>
        <Link
          to="/"
          className="mb-8 inline-flex items-center transition-opacity hover:opacity-80"
        >
          <img src={logo} alt="Sqaleshop" className="h-9 w-auto" />
        </Link>

        <ProgressSteps step={step} total={3} className="mb-6 sm:mb-8" />

        <AuthPageHeader
          title={
            step === 1
              ? "Business Details"
              : step === 2
                ? "Your Goals with Sqaleshop"
                : "Store Information"
          }
          description={
            step === 1
              ? "Tell us about your business"
              : step === 2
                ? "Select what you want to achieve"
                : "Set up your store details"
          }
          showLogo={false}
          className="mb-6 sm:mb-8"
        />

        <form
          onSubmit={step === 3 ? handleSubmit : (e) => e.preventDefault()}
          className="space-y-3"
        >
          {/* Step 1: Business Details */}
          {step === 1 && (
            <>
              <div className="space-y-1">
                <label
                  htmlFor={getInputId("businessName")}
                  className="text-sm font-medium"
                >
                  Business Name
                </label>
                <div className="flex items-center gap-2 w-full mt-1">
                  <Input
                    id={getInputId("businessName")}
                    value={formData.businessName}
                    onChange={(e) =>
                      setFormData({ ...formData, businessName: e.target.value })
                    }
                    placeholder="Enter your business name"
                    required
                    className={cn("w-full", authFieldClass)}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium mb-2">
                  Type of Business
                </label>
                <Select
                  value={formData.businessType}
                  onValueChange={(value) =>
                    setFormData({ ...formData, businessType: value })
                  }
                  required
                >
                  <SelectTrigger className={cn("mt-1 w-full", authFieldClass)}>
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <span className="inline-flex items-center gap-2">
                          <span>{type.icon}</span>
                          {type.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium">
                  Number of Employees
                </label>
                <Select
                  value={formData.numberOfEmployees}
                  onValueChange={(value) =>
                    setFormData({ ...formData, numberOfEmployees: value })
                  }
                  required
                >
                  <SelectTrigger className={cn("mt-1 w-full", authFieldClass)}>
                    <SelectValue placeholder="Select number of employees" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYEE_COUNT_OPTIONS.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium">
                  Monthly Orders/Bookings
                </label>
                <Select
                  value={formData.monthlyOrders}
                  onValueChange={(value) =>
                    setFormData({ ...formData, monthlyOrders: value })
                  }
                  required
                >
                  <SelectTrigger className={cn("mt-1 w-full", authFieldClass)}>
                    <SelectValue placeholder="Select monthly orders" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHLY_ORDERS_OPTIONS.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium mb-2">
                  How did you hear about us?
                </label>
                <Select
                  value={formData.referralSource}
                  onValueChange={(value) =>
                    setFormData({ ...formData, referralSource: value })
                  }
                  required
                >
                  <SelectTrigger className={cn("w-full", authFieldClass)}>
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    {REFERRAL_OPTIONS.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Step 2: Goals with Sqaleshop */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4 font-medium">
                Select all that apply to your business needs
              </p>

              <div className="grid grid-cols-1 gap-3">
                {GOAL_OPTIONS.map((goal) => (
                  <div
                    key={goal.id}
                    className="flex h-auto min-h-12 items-center space-x-3 rounded-2xl border border-primary/15 bg-[#faf9f7] px-4 py-3.5"
                  >
                    <Checkbox
                      className="cursor-pointer"
                      id={goal.id}
                      checked={formData.goals.includes(goal.id)}
                      onCheckedChange={() => handleGoalToggle(goal.id)}
                    />
                    <label
                      htmlFor={goal.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {goal.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Store Information */}
          {step === 3 && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Store Name</label>
                <div className="flex items-center gap-2 w-full mt-1">
                 
                  <Input
                    value={formData.storeName}
                    onChange={(e) =>
                      setFormData({ ...formData, storeName: e.target.value })
                    }
                    placeholder="Enter your store name"
                    required
                    className={cn("w-full", authFieldClass)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium mb-1">Store Logo</label>
                <LogoUpload
                  value={logoFile}
                  onChange={(file) => {
                    setLogoFile(file);
                    // Clear base64 when file is null
                    if (!file) {
                      setLogoBase64(null);
                    }
                  }}
                  initialPreview={logoBase64 || undefined}
                  onProcessed={(base64Data) => {
                    setLogoBase64(base64Data);
                  }}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">WhatsApp Number</label>
                <PhoneInput
                  international
                  defaultCountry="NG"
                  className="mt-1"
                  value={formData.whatsappNumber}
                  onChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      whatsappNumber: value || "",
                    }));
                    setPhoneError("");
                  }}
                  placeholder="Enter your WhatsApp number"
                  required
                  error={phoneError}
                />
                {phoneError && (
                  <p className="text-sm text-destructive">{phoneError}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Currency</label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) =>
                    setFormData({ ...formData, currency: value })
                  }
                  required
                >
                  <SelectTrigger className={cn("mt-1 w-full", authFieldClass)}>
                    
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((currency) => (
                      <SelectItem key={currency.id} value={currency.id}>
                        {currency.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Store Link</label>
                <div className="flex items-center gap-2 w-full">
                  <Globe className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 flex items-center gap-1 text-sm w-full">
                    <span className="text-muted-foreground flex-shrink-0">
                      sqale.shop/
                    </span>
                    <Input
                      value={formData.url}
                      onChange={handleUrlChange}
                      placeholder="mystore"
                      required
                      className={cn(
                        "w-full",
                        authFieldClass,
                        urlError && "border-destructive",
                      )}
                    />
                  </div>
                </div>
                {urlError ? (
                  <p className="text-xs text-destructive mt-1">{urlError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.url
                      ? `This will be your sales link: sqale.shop/${formData.url}. You can change it later.`
                      : "Choose a short link customers can remember."}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 sm:gap-4">
            {step > 1 && (
              <Button
                type="button"
                onClick={handleBack}
                variant="outline"
                className={cn("flex-1", authOutlineButtonClass)}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}

            {step < 3 ? (
              <Button
                type="button"
                onClick={handleNext}
                className={cn("flex-1", authButtonClass)}
              >
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="submit"
                className={cn("flex-1", authButtonClass)}
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : "Complete Setup"}
              </Button>
            )}
          </div>
        </form>
      </AuthCard>
    </AuthLayout>
  );
}
